import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { Button, Tag } from "../../../../design-system";
import {
  disconnectGoogleCalendar,
  getGoogleCalendarConnection,
  GoogleCalendarConnection,
  startGoogleCalendarAuth,
} from "../../../../database/booking/googleCalendarApi";

const Panel = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
`;

const Copy = styled.div`
  flex: 1 1 320px;
`;

const Title = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Detail = styled.div`
  margin-top: var(--space-1);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const ErrorText = styled.div`
  flex-basis: 100%;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-danger);
`;

export const GoogleCalendarConnectPanel: React.FC<{ communityId?: string }> = ({
  communityId,
}) => {
  const [connection, setConnection] = useState<GoogleCalendarConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError("");
    try {
      setConnection(await getGoogleCalendarConnection(communityId));
    } catch (err: any) {
      setError(err?.message || "Could not load Google Calendar status.");
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const connect = async () => {
    if (!communityId) return;
    // Open synchronously so Safari/Chrome do not block the popup while the
    // callable mints the short-lived OAuth state.
    const popup = window.open("about:blank", "tribed-google-calendar");
    setBusy(true);
    setError("");
    try {
      const { url } = await startGoogleCalendarAuth(communityId);
      if (popup) popup.location.assign(url);
      else window.location.assign(url);
    } catch (err: any) {
      if (popup) popup.close();
      setError(err?.message || "Could not start the Google Calendar connection.");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!communityId || !window.confirm("Disconnect this Google Calendar?")) return;
    setBusy(true);
    setError("");
    try {
      await disconnectGoogleCalendar(communityId);
      await refresh();
    } catch (err: any) {
      setError(err?.message || "Could not disconnect Google Calendar.");
    } finally {
      setBusy(false);
    }
  };

  const connected = Boolean(connection?.connected);
  return (
    <Panel>
      <Copy>
        <Title>Google Calendar</Title>
        <Detail>
          {loading && !connection
            ? "Checking connection…"
            : connected
            ? `${connection?.email || "Connected"} · busy times are blocked and confirmed bookings are added automatically.`
            : "Connect the coach’s calendar to prevent conflicts and add confirmed bookings automatically."}
        </Detail>
      </Copy>
      {connected ? <Tag>{connection?.stale ? "Reconnect required" : "Connected"}</Tag> : null}
      {connected && !connection?.stale ? (
        <Button variant="secondary" size="sm" onClick={disconnect} disabled={busy}>
          Disconnect
        </Button>
      ) : (
        <Button variant="primary" size="sm" onClick={connect} disabled={busy || !communityId}>
          {busy ? "Opening…" : connection?.stale ? "Reconnect Google Calendar" : "Connect Google Calendar"}
        </Button>
      )}
      {connection?.staleReason ? <ErrorText>{connection.staleReason}</ErrorText> : null}
      {error ? <ErrorText>{error}</ErrorText> : null}
    </Panel>
  );
};

export default GoogleCalendarConnectPanel;
